<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { apiClient, ApiError, NetworkError } from "@/api/client";
import type {
  FocusBlockCloseRequest,
  FocusBlockOpenRequest,
  FocusProfileResponse,
  MockQuestion,
  MockSubmitResponse,
  QuestionResponse,
  TopicNode,
} from "@/api/types";
import BreakScreen from "@/components/session/BreakScreen.vue";
import FocusBar from "@/components/session/FocusBar.vue";
import InactivityNudge from "@/components/session/InactivityNudge.vue";
import MockResults from "@/components/session/MockResults.vue";
import MockRunner from "@/components/session/MockRunner.vue";
import QuestionPanel from "@/components/session/QuestionPanel.vue";
import SessionBootLoader from "@/components/session/SessionBootLoader.vue";
import SessionComplete from "@/components/session/SessionComplete.vue";
import SessionPlan from "@/components/session/SessionPlan.vue";
import TutorPanel from "@/components/session/TutorPanel.vue";
import OverlayShell from "@/components/ui/OverlayShell.vue";
import RlCard from "@/components/ui/RlCard.vue";
import { logEvent } from "@/lib/analytics/statsig";
import {
  ATTAINMENT_LABEL,
  tierFor,
  type AttainmentTier,
} from "@/lib/attainment";
import type { BootMilestone } from "@/lib/bootMilestones";
import { completionOutcome, isStageUp, stageFor } from "@/lib/focusCapacity";
import { preloadMath } from "@/lib/markdown";
import {
  DEFAULT_CONFIG,
  formatClock,
  goalFraction,
  goalMinutes,
  goalSeconds,
  initialState,
  isGoalMet,
  isStretchSelected,
  reduce,
  type EndedReason,
  type FocusBlockRecord,
  type FocusSessionConfig,
  type FocusSessionEvent,
  type FocusSessionState,
  type GoalType,
  type SessionMode,
  type SessionScope,
} from "@/lib/focusSession";
import { useFocusStore } from "@/stores/focus";
import { useHomeStore } from "@/stores/home";
import {
  FOCUS_BLOCK_COMPLETED,
  FOCUS_BREAK_TAKEN,
  FOCUS_NUDGE_SHOWN,
  FOCUS_SESSION_ENDED,
  FOCUS_SESSION_STARTED,
  SESSION_OPENED,
  type EventProperties,
} from "@shared/analytics/events";

/**
 * The session overlay (PRD §6.8). A session is a *verb*: this mounts once in
 * the shell and activates on `?session=1` for the current subject, so it always
 * renders over whatever the student was looking at and never becomes a route.
 *
 * This component is the single owner of session state, the one-second tick, the
 * `/focus-blocks` write path and every focus analytics event. Its children are
 * presentational — they take props and emit events, and none of them touches
 * session state or the focus-block endpoints.
 */

const route = useRoute();
const router = useRouter();
const home = useHomeStore();
const focus = useFocusStore();

/**
 * Ten questions at three minutes each. GCSE papers run at roughly a mark a
 * minute and our generated questions average about three marks, so this is the
 * pacing a real paper imposes rather than an arbitrary countdown.
 */
const MOCK_QUESTIONS = 10;
const MOCK_MINUTES = 30;

/** Named past papers are deferred (D10): the picker takes a list, one ships. */
const PAPERS = [{ name: "Mixed questions", meta: "adaptive" }];

// ── Route activation ──────────────────────────────────────

const subjectId = computed(() => {
  const id = route.params.subjectId;
  return typeof id === "string" && id.length > 0 ? id : null;
});

const topicParam = computed(() =>
  typeof route.query.topic === "string" && route.query.topic.length > 0
    ? route.query.topic
    : null,
);

/** The subject the overlay is open for, or null when it should be closed. */
const activeSubject = computed(() =>
  route.query.session === "1" ? subjectId.value : null,
);

// ── Session state ─────────────────────────────────────────

const state = ref<FocusSessionState>(initialState());
/** `mockSeconds` is replaced per mock with the limit the API actually granted. */
const config = ref<FocusSessionConfig>({ ...DEFAULT_CONFIG });

function dispatch(event: FocusSessionEvent): void {
  state.value = reduce(state.value, event, config.value);
}

const open = computed(
  () => activeSubject.value !== null && state.value.phase !== "idle",
);

// ── Names, capacity, boot artefacts ───────────────────────

const subjectName = ref("");
const topicName = ref<string | null>(null);
const topics = ref<TopicNode[]>([]);
const suggestedTopicId = ref<string | null>(null);

/**
 * The focus profile as it stood when the overlay opened, kept separate from
 * {@link laterProfile} so the completion screen compares against the numbers
 * the session started from rather than the ones it has just moved.
 */
const openProfile = ref<FocusProfileResponse | null>(null);
const laterProfile = ref<FocusProfileResponse | null>(null);

const capacityMinutes = computed(
  () => openProfile.value?.capacity_minutes ?? null,
);

/**
 * Capacity frozen at confirm. The launcher derived its options from this
 * number, so goal progress keeps using it: the post-session refetch must not be
 * able to move the goalpost retroactively.
 */
const lockedCapacity = ref<number | null>(null);

const bootMilestone = ref<BootMilestone>("idle");
const bootError = ref<string | null>(null);
const sessionId = ref<string | null>(null);
const firstQuestion = ref<QuestionResponse | null>(null);
const tutorOpening = ref<string | null>(null);
const mockQuestions = ref<MockQuestion[]>([]);
const mockTotalSeconds = ref(0);
const mockResult = ref<MockSubmitResponse | null>(null);
const beforeTier = ref<AttainmentTier | null>(null);
const afterTier = ref<AttainmentTier | null>(null);
const confirmClose = ref(false);

let openedAtMs = 0;

// ── Derived labels ────────────────────────────────────────

/**
 * The machine owns the ids; the name refs own the labels, which can land after
 * the overlay is already on screen.
 */
const displayScope = computed<SessionScope | null>(() => {
  const scope = state.value.scope;
  if (scope === null) return null;
  return {
    ...scope,
    subjectName: subjectName.value || scope.subjectName,
    topicName: topicName.value ?? scope.topicName,
  };
});

const focusName = computed(
  () => displayScope.value?.topicName ?? displayScope.value?.subjectName ?? "",
);

const scopeLabel = computed(() => {
  const scope = displayScope.value;
  if (scope === null) return "";
  return scope.topicName
    ? `${scope.subjectName} · ${scope.topicName}`
    : scope.subjectName;
});

/** What `focus_blocks.mode` and analytics take. Null for a mock. */
const blockMode = computed<"practice" | "tutor" | null>(() => {
  switch (state.value.mode) {
    case "question":
      return "practice";
    case "tutor":
      return "tutor";
    default:
      return null;
  }
});

const analyticsMode = computed(() => blockMode.value ?? "mock");

const goalLabel = computed(() => {
  const s = state.value;
  if (s.goalType === "marks") return `${s.marksEarned} / ${s.goalMarks} marks`;
  return `${formatClock(s.activeSeconds)} / ${formatClock(goalSeconds(s, lockedCapacity.value))}`;
});

const focusMeta = computed(() => {
  const s = state.value;
  const block = `Block ${(s.openBlock?.blockIndex ?? s.completedBlocks.length) + 1}`;
  if (s.mode === "tutor") {
    return `${block} · ${s.exchanges} ${s.exchanges === 1 ? "exchange" : "exchanges"}`;
  }
  return `${block} · ${s.questionsAnswered} answered · ${s.marksEarned} marks`;
});

const hasWorkInFlight = computed(
  () => state.value.openBlock !== null || state.value.phase === "mockRunning",
);

const confirmCopy = computed(() =>
  state.value.phase === "mockRunning"
    ? "Close this mock? Your answers won't be marked."
    : "Close the session? This block won't count towards your focus capacity.",
);

// ── The one timer ─────────────────────────────────────────

/**
 * One interval for the whole overlay, running only while a phase actually
 * counts seconds. An untimed mock never ticks: the reducer would read a zero
 * `mockSecondsLeft` as time up and end the paper on the first tick.
 */
const needsTicker = computed(() => {
  const phase = state.value.phase;
  if (phase === "focusing" || phase === "break") return true;
  return phase === "mockRunning" && mockTotalSeconds.value > 0;
});

let ticker: ReturnType<typeof setInterval> | null = null;

function startTicker(): void {
  if (ticker !== null) return;
  ticker = setInterval(
    () => dispatch({ type: "tick", atMs: Date.now() }),
    1000,
  );
}

function stopTicker(): void {
  if (ticker === null) return;
  clearInterval(ticker);
  ticker = null;
}

watch(needsTicker, (wanted) => (wanted ? startTicker() : stopTicker()), {
  immediate: true,
});

// ── Analytics ─────────────────────────────────────────────

/**
 * Every focus event is built from this base so no call site can invent a key.
 * `capacity_minutes` is omitted rather than zeroed when there is no honest
 * median — a 0 reads as a measured collapse (PE-7.2). The completion reflection
 * and any goal free text are local-only and appear nowhere near these payloads;
 * the payload guard denylists them as a backstop.
 */
function baseProps(): EventProperties {
  const props: EventProperties = { mode: analyticsMode.value };
  const scope = state.value.scope;
  if (scope !== null) {
    props.course_id = scope.subjectId;
    if (scope.topicId) props.topic = scope.topicId;
  }
  return props;
}

function withFocusContext(props: EventProperties): EventProperties {
  const capacity = lockedCapacity.value ?? capacityMinutes.value;
  if (capacity !== null) props.capacity_minutes = capacity;
  props.stage = stageFor(capacity).name.toLowerCase();
  return props;
}

function logSessionStarted(): void {
  // A mock is deliberately absent from the focus funnel: it opens no block and
  // its time never reaches the capacity median, exactly as the API refuses
  // mode='mock' on /focus-blocks. Counting it as a focus session started would
  // put exam time into the metric the whole growth story rests on.
  if (blockMode.value === null) return;
  const props = withFocusContext(baseProps());
  props.goal_type = state.value.goalType;
  props.stretch_selected = isStretchSelected(state.value, lockedCapacity.value);
  logEvent(FOCUS_SESSION_STARTED, undefined, props);
}

function logBlockCompleted(record: FocusBlockRecord): void {
  const props = baseProps();
  props.block_index = record.blockIndex;
  props.ended_reason = record.endedReason;
  props.goal_met = record.goalMet;
  props.active_seconds = record.activeSeconds;
  props.idle_seconds = record.idleSecondsTotal;
  logEvent(FOCUS_BLOCK_COMPLETED, undefined, props);
}

/** Reads live state, so it must run *before* a reset-bearing dispatch. */
function logSessionEnded(reason: EndedReason, goalMet: boolean): void {
  if (blockMode.value === null) return;
  const props = withFocusContext(baseProps());
  props.ended_reason = reason;
  props.goal_met = goalMet;
  props.active_seconds = state.value.activeSeconds;
  props.idle_seconds = state.value.idleSeconds;
  logEvent(FOCUS_SESSION_ENDED, undefined, props);
}

watch(
  () => state.value.nudgeVisible,
  (visible) => {
    if (!visible) return;
    const props = baseProps();
    props.block_index = state.value.openBlock?.blockIndex ?? 0;
    logEvent(FOCUS_NUDGE_SHOWN, undefined, props);
  },
);

// ── Focus block write path ────────────────────────────────

/**
 * Everything a block write needs, captured once per session. The queue below
 * must never read reactive state: a student who closes the overlay and
 * relaunches immediately would otherwise have the first session's records
 * flushed against the second session's (already reset) state.
 */
interface BlockContext {
  mode: "practice" | "tutor";
  courseId: string;
  courseTopicId: string | null;
  goalType: GoalType;
  goalValue: number;
  stretchSelected: boolean;
  sessionId: string;
}

let blockContext: BlockContext | null = null;

/**
 * `focus_blocks` rows are the only substrate for `capacity_minutes` and nothing
 * here can be reconstructed later, so writes are serialised through one chain:
 * the POST opening block N+1 must never overtake the PATCH closing block N.
 */
let chain: Promise<void> = Promise.resolve();
/** The server id of the block currently open. Only the chain touches it. */
let openBlockId: string | null = null;

function enqueue(work: () => Promise<void>): void {
  chain = chain.then(work).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[focus] block write failed", err);
  });
}

function openBlockBody(
  ctx: BlockContext,
  blockIndex: number,
): FocusBlockOpenRequest {
  return {
    tutor_session_id: ctx.sessionId,
    block_index: blockIndex,
    course_id: ctx.courseId,
    course_topic_id: ctx.courseTopicId,
    mode: ctx.mode,
    goal_type: ctx.goalType,
    goal_value: ctx.goalValue,
    stretch_selected: ctx.stretchSelected,
  };
}

function closeBlockBody(record: FocusBlockRecord): FocusBlockCloseRequest {
  return {
    active_seconds: record.activeSeconds,
    ended_reason: record.endedReason,
    goal_met: record.goalMet,
    idle_seconds_total: record.idleSecondsTotal,
    nudge_shown: record.nudgeShown,
    nudge_dismissed: record.nudgeDismissed,
    break_offered: record.breakOffered,
    break_taken: record.breakTaken,
    break_skipped: record.breakSkipped,
  };
}

function enqueueOpenBlock(blockIndex: number): void {
  const ctx = blockContext;
  if (ctx === null) return;
  enqueue(async () => {
    if (openBlockId !== null) return;
    openBlockId = (
      await apiClient.openFocusBlock(openBlockBody(ctx, blockIndex))
    ).id;
  });
}

function enqueueCloseBlock(record: FocusBlockRecord): void {
  const ctx = blockContext;
  if (ctx === null) return;
  enqueue(async () => {
    let id = openBlockId;
    openBlockId = null;
    if (id === null) {
      // The opening POST failed or never ran. Recover by opening the row now
      // and closing it immediately: a late row still counts towards capacity,
      // a lost one never comes back.
      id = (
        await apiClient.openFocusBlock(openBlockBody(ctx, record.blockIndex))
      ).id;
    }
    await apiClient.closeFocusBlock(id, closeBlockBody(record));
  });
}

/**
 * A block closed by `break_taken`, held until the break resolves. `skipBreak`
 * backfills `break_skipped` onto this record, and the server's close is
 * first-write-wins, so sending it at break start would permanently record a
 * cut-short break as a full one.
 */
let heldRecord: FocusBlockRecord | null = null;

function releaseHeldRecord(): void {
  if (heldRecord === null) return;
  enqueueCloseBlock(heldRecord);
  heldRecord = null;
}

/**
 * Sync flush, not the default: block bookkeeping has to observe every
 * transition in order. A post-tick snapshot would coalesce "block closed" and
 * "session reset" into one callback and lose the record between them.
 */
watch(
  () => state.value.completedBlocks,
  (records, previous) => {
    const before = previous?.length ?? 0;
    if (records.length > before) {
      for (let i = before; i < records.length; i += 1) {
        const record = records[i];
        logBlockCompleted(record);
        if (record.endedReason === "break_taken") heldRecord = record;
        else enqueueCloseBlock(record);
      }
      return;
    }
    // Same length, new array: `skipBreak` rewrote the record we are holding.
    const last = records[records.length - 1];
    if (
      heldRecord !== null &&
      last !== undefined &&
      last.blockIndex === heldRecord.blockIndex
    ) {
      heldRecord = last;
    }
  },
  { flush: "sync" },
);

watch(
  () => state.value.phase,
  (phase) => {
    if (phase !== "break") releaseHeldRecord();
  },
  { flush: "sync" },
);

watch(
  () => state.value.openBlock?.blockIndex ?? null,
  (index) => {
    if (index !== null) enqueueOpenBlock(index);
  },
  { flush: "sync" },
);

function abandonedSnapshot(
  block: NonNullable<FocusSessionState["openBlock"]>,
): FocusBlockRecord {
  return {
    blockIndex: block.blockIndex,
    startedAtMs: block.startedAtMs,
    endedAtMs: Date.now(),
    activeSeconds: block.activeSeconds,
    idleSecondsTotal: block.idleSeconds,
    endedReason: "abandoned",
    goalMet: false,
    nudgeShown: block.nudgeShown,
    nudgeDismissed: block.nudgeDismissed,
    breakOffered: block.breakOffered,
    breakTaken: block.breakTaken,
    breakSkipped: block.breakSkipped,
  };
}

/**
 * `pagehide` rather than `visibilitychange`: the server's close is
 * first-write-wins, so a speculative close fired on a tab switch would freeze a
 * live block as abandoned. `persisted` means the page went into the
 * back-forward cache and can still come back, so only a real teardown flushes.
 *
 * A keepalive fetch rather than `sendBeacon` because these endpoints need the
 * Authorization header, which `sendBeacon` cannot carry.
 */
function handlePageHide(event: PageTransitionEvent): void {
  if (event.persisted) return;
  const id = openBlockId;
  const record =
    heldRecord ??
    (state.value.openBlock && abandonedSnapshot(state.value.openBlock));
  if (id !== null && record) {
    openBlockId = null;
    heldRecord = null;
    void apiClient
      .closeFocusBlock(id, closeBlockBody(record), true)
      .catch(() => {
        /* the page is going away; there is nothing left to retry with */
      });
  }
  endSessionOnce(sessionId.value, true);
}

// ── Session status ────────────────────────────────────────

const endedSessions = new Set<string>();

/**
 * Flips `TutorSession.status` from active to completed. Once per created
 * session, not once per block — a session with three blocks ends once — and
 * best-effort everywhere: a failed status update must never stop a student
 * closing the overlay or leaving the page.
 */
function endSessionOnce(id: string | null, keepalive = false): void {
  if (id === null || endedSessions.has(id)) return;
  endedSessions.add(id);
  const request = keepalive
    ? apiClient.endSessionKeepalive(id)
    : apiClient.endSession(id);
  void request.catch(() => {
    /* best-effort */
  });
}

// ── Open, boot, close ─────────────────────────────────────

function resetArtifacts(): void {
  // A previous mock may have narrowed `mockSeconds` to the limit the API
  // granted; the next session must not inherit it.
  config.value = { ...DEFAULT_CONFIG };
  bootMilestone.value = "idle";
  bootError.value = null;
  sessionId.value = null;
  firstQuestion.value = null;
  tutorOpening.value = null;
  mockQuestions.value = [];
  mockTotalSeconds.value = 0;
  mockResult.value = null;
  laterProfile.value = null;
  afterTier.value = null;
  confirmClose.value = false;
  lockedCapacity.value = null;
  blockContext = null;
}

function openSession(id: string): void {
  openedAtMs = Date.now();
  resetArtifacts();
  openProfile.value = null;
  topics.value = [];
  suggestedTopicId.value = null;
  beforeTier.value = null;
  subjectName.value =
    home.subjects.find((s) => s.subject_id === id)?.title ?? "";
  topicName.value = null;
  // Through the chain, not directly: a previous session's closing PATCH may
  // still be queued and it needs the id it was opened with.
  enqueue(async () => {
    openBlockId = null;
  });

  const topicId = topicParam.value;
  dispatch({
    type: "open",
    scope: {
      kind: topicId === null ? "subject" : "topic",
      subjectId: id,
      subjectName: subjectName.value,
      topicId: topicId ?? undefined,
    },
  });

  void loadProfile();
  void resolveNames(id);
}

async function loadProfile(): Promise<void> {
  try {
    openProfile.value = await apiClient.getFocusProfile();
  } catch {
    // No profile means no honest median, which is the same state as a cold
    // start: the launcher shows the fixed ladder and claims no capacity.
    openProfile.value = null;
  }
}

async function resolveNames(id: string): Promise<void> {
  if (!subjectName.value) {
    await home.load();
    subjectName.value =
      home.subjects.find((s) => s.subject_id === id)?.title ?? "";
  }
  if (!subjectName.value) {
    try {
      subjectName.value = (await apiClient.getSubject(id)).title;
    } catch {
      /* the scope line degrades to the topic; the session still works */
    }
  }
  try {
    const profile = await apiClient.getLearningProfile(id);
    topics.value = profile.topics;
    suggestedTopicId.value =
      profile.recommendations[0]?.topic_id ?? profile.topics[0]?.id ?? null;
  } catch {
    return;
  }
  const scoped = state.value.scope?.topicId;
  if (scoped) applyTopic(scoped);
}

/** Names a topic for display, records its tier, and tells the machine about it. */
function applyTopic(id: string): void {
  const topic = topics.value.find((t) => t.id === id);
  topicName.value = topic?.display_name ?? null;
  beforeTier.value = topic ? tierFor(topic.stats) : null;
  dispatch({
    type: "setScopeTopic",
    topicId: id,
    topicName: topicName.value ?? "",
  });
}

/**
 * A subject-scoped launch promised "we'll pick the topic that needs it most" —
 * this keeps that promise, from the same recommendation the subject page ranks
 * by.
 */
async function ensureTopic(id: string): Promise<string> {
  const scoped = state.value.scope?.topicId;
  if (scoped) return scoped;
  if (suggestedTopicId.value === null) {
    const profile = await apiClient.getLearningProfile(id);
    topics.value = profile.topics;
    suggestedTopicId.value =
      profile.recommendations[0]?.topic_id ?? profile.topics[0]?.id ?? null;
  }
  const picked = suggestedTopicId.value;
  if (picked === null) {
    throw new Error(
      "This subject has no topics yet, so there's nothing to work on.",
    );
  }
  applyTopic(picked);
  return picked;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError)
    return error.code ?? `Something went wrong (${error.status}).`;
  // The browser's own wording ("Failed to fetch", "Load failed") names the
  // transport, not the problem, and differs per browser (rl-gmq).
  if (error instanceof NetworkError)
    return "We couldn’t reach RevisionLab. Check your connection and try again.";
  if (error instanceof Error) return error.message;
  return "Something went wrong starting your session.";
}

async function boot(): Promise<void> {
  const id = subjectId.value;
  if (id === null) return;
  bootError.value = null;
  // A retry after a failure that already created a session would otherwise
  // leave that one 'active' forever, since the next attempt creates its own.
  endSessionOnce(sessionId.value);
  bootMilestone.value = "requested";
  try {
    if (state.value.mode === "mock") {
      bootMilestone.value = "generating";
      const body: {
        num_questions: number;
        topic_ids?: string[];
        time_limit_minutes?: number;
      } = {
        num_questions: MOCK_QUESTIONS,
        time_limit_minutes: MOCK_MINUTES,
      };
      const scoped = state.value.scope?.topicId;
      if (scoped) body.topic_ids = [scoped];
      const created = await apiClient.startMockSession(id, body);
      sessionId.value = created.session_id;
      mockQuestions.value = created.questions;
      mockTotalSeconds.value = created.time_limit_minutes
        ? created.time_limit_minutes * 60
        : 0;
      // The machine reads the limit from config, so it has to be in place
      // before `booted` moves the phase to mockRunning.
      config.value = { ...config.value, mockSeconds: mockTotalSeconds.value };
    } else if (state.value.mode === "tutor") {
      const topicId = await ensureTopic(id);
      bootMilestone.value = "generating";
      const created = await apiClient.startTutorSession(id, {
        topic_id: topicId,
      });
      sessionId.value = created.session_id;
      tutorOpening.value = created.question_text;
    } else {
      const topicId = await ensureTopic(id);
      const created = await apiClient.startPracticeSession(id);
      sessionId.value = created.session_id;
      bootMilestone.value = "session_created";
      bootMilestone.value = "generating";
      firstQuestion.value = await apiClient.generateGuidedQuestion(id, {
        topic_id: topicId,
      });
    }

    prepareBlockContext(id);
    bootMilestone.value = "ready";
    // The cold start is the wait we promised to measure rather than fake
    // (PE-8.3). It has no whitelisted analytics key, and a duration this coarse
    // would need a privacy review before becoming a Statsig dimension.
    // eslint-disable-next-line no-console
    console.info(
      "container_cold_start_ms",
      Date.now() - openedAtMs,
      analyticsMode.value,
    );

    dispatch({ type: "booted", atMs: Date.now() });
    logEvent(SESSION_OPENED, undefined, {
      course_id: id,
      mode: analyticsMode.value,
    });
    logSessionStarted();
  } catch (error) {
    bootMilestone.value = "failed";
    bootError.value = describeError(error);
  }
}

/** Captured before `booted` so the first block's POST has everything it needs. */
function prepareBlockContext(courseId: string): void {
  const mode = blockMode.value;
  const session = sessionId.value;
  if (mode === null || session === null) {
    blockContext = null;
    return;
  }
  const s = state.value;
  blockContext = {
    mode,
    courseId,
    courseTopicId: s.scope?.topicId ?? null,
    goalType: s.goalType,
    goalValue:
      s.goalType === "marks"
        ? s.goalMarks
        : goalMinutes(s, lockedCapacity.value),
    stretchSelected: isStretchSelected(s, lockedCapacity.value),
    sessionId: session,
  };
}

async function stripParams(): Promise<void> {
  const query = { ...route.query };
  delete query.session;
  delete query.topic;
  // `replace`, not `push`: a back-navigation after a session returns to
  // wherever the student came from rather than reopening the overlay.
  await router.replace({ path: route.path, query });
}

/** Everything that has to happen when the overlay stops being open. */
function teardown(): void {
  const s = state.value;
  if (s.openBlock !== null || s.phase === "mockRunning") {
    logSessionEnded("abandoned", false);
  }
  const session = sessionId.value;
  // Closing mid-block records an abandoned block; the sync watchers above turn
  // it into a PATCH before this function returns.
  dispatch({ type: "close", atMs: Date.now() });
  enqueue(async () => {
    endSessionOnce(session);
  });
  stopTicker();
  resetArtifacts();
}

watch(
  activeSubject,
  (id, previous) => {
    if (previous != null && id !== previous) teardown();
    if (id != null && id !== previous) openSession(id);
  },
  { immediate: true },
);

function requestClose(): void {
  if (confirmClose.value) {
    confirmClose.value = false;
    return;
  }
  if (hasWorkInFlight.value) {
    confirmClose.value = true;
    return;
  }
  void stripParams();
}

/** Confirmed close. Stripping the params is what triggers {@link teardown}. */
function closeNow(): void {
  confirmClose.value = false;
  void stripParams();
}

// ── Completion ────────────────────────────────────────────

const completeMinutes = computed(() =>
  Math.round(state.value.activeSeconds / 60),
);

/**
 * The longest block this session actually held, in minutes. Abandoned blocks
 * are excluded, matching what the server counts towards capacity.
 */
const sessionBlockMinutes = computed(() => {
  let longest = 0;
  for (const record of state.value.completedBlocks) {
    if (record.endedReason === "abandoned") continue;
    longest = Math.max(longest, record.activeSeconds);
  }
  return Math.round(longest / 60);
});

const capacityOutcome = computed(() =>
  completionOutcome({
    blockMinutes: sessionBlockMinutes.value,
    capacityMinutes: lockedCapacity.value,
    longestMinutes: openProfile.value?.longest_block_minutes ?? 0,
  }),
);

const capacityAfter = computed(() =>
  laterProfile.value === null
    ? lockedCapacity.value
    : laterProfile.value.capacity_minutes,
);

const stageIndex = computed(() => stageFor(capacityAfter.value).index);
const stageUp = computed(() =>
  isStageUp(lockedCapacity.value, capacityAfter.value),
);

const attainmentChange = computed(() => {
  const before = beforeTier.value;
  const after = afterTier.value;
  const topic = topicName.value;
  if (before === null || after === null || topic === null || before === after)
    return null;
  return `${topic} moved from ${ATTAINMENT_LABEL[before]} to ${ATTAINMENT_LABEL[after]}`;
});

/**
 * A gesture, not a scheduler: adaptive spaced review needs real review history
 * and is deferred until after beta (handoff §5).
 */
const reviewNudge = computed(
  () =>
    `Come back to ${focusName.value} in a few days — spacing it out is what makes it stick.`,
);

async function refreshOutcome(): Promise<void> {
  try {
    laterProfile.value = await apiClient.getFocusProfile();
    // Today's Focus card reads the shared store, so hand it the capacity we
    // just fetched. Without this a student closes the overlay onto the number
    // they had before the block they just finished.
    focus.adopt(laterProfile.value);
  } catch {
    laterProfile.value = null;
  }
  const id = subjectId.value;
  const topicId = state.value.scope?.topicId;
  if (id === null || !topicId) return;
  try {
    const profile = await apiClient.getLearningProfile(id);
    const topic = profile.topics.find((t) => t.id === topicId);
    afterTier.value = topic ? tierFor(topic.stats) : null;
  } catch {
    afterTier.value = null;
  }
}

watch(
  () => state.value.phase,
  async (phase) => {
    if (phase !== "complete") return;
    const reason = state.value.endedReason ?? "ended_early";
    logSessionEnded(reason, reason === "goal_met");
    const session = sessionId.value;
    // The closing PATCH has to land before we ask for a new capacity, or the
    // block that just finished would be missing from the median we report.
    await chain;
    endSessionOnce(session);
    await refreshOutcome();
  },
);

// ── Child events ──────────────────────────────────────────

function onTakeBreak(): void {
  const props = baseProps();
  props.block_index = state.value.openBlock?.blockIndex ?? 0;
  dispatch({
    type: "takeBreak",
    atMs: Date.now(),
    goalMet: isGoalMet(state.value, lockedCapacity.value),
  });
  logEvent(FOCUS_BREAK_TAKEN, undefined, props);
}

function onEnd(): void {
  dispatch({
    type: "end",
    atMs: Date.now(),
    goalMet: isGoalMet(state.value, lockedCapacity.value),
  });
}

function onConfirmPlan(): void {
  lockedCapacity.value = capacityMinutes.value;
  dispatch({ type: "confirm" });
  void boot();
}

function onMockSubmitted(result: MockSubmitResponse): void {
  mockResult.value = result;
  // On expiry the reducer has already moved to mockResults; only a manual
  // submit still needs the transition.
  if (state.value.phase === "mockRunning") dispatch({ type: "submitMock" });
  endSessionOnce(sessionId.value);
}

function restart(): void {
  const id = subjectId.value;
  if (id === null) return;
  openSession(id);
}

// ── Lifecycle ─────────────────────────────────────────────

onMounted(() => {
  window.addEventListener("pagehide", handlePageHide);
  // Start fetching KaTeX the moment the overlay opens. Questions arrive after a
  // multi-second API round trip behind the boot loader, so the maths renderer
  // is in place before any question text can reach the DOM — the student never
  // sees the un-rendered `$…$` fallback.
  void preloadMath();
});

onBeforeUnmount(() => {
  stopTicker();
  window.removeEventListener("pagehide", handlePageHide);
});
</script>

<template>
  <!--
    `data-statsig-block` marks tutor questions, mark schemes, typed answers and
    grades as never to be recorded (PE-9.5, extending rl-eee.7). Autocapture is
    kept off this surface by the `/subjects/:id` URL filter in `statsig.ts`, and
    there is no session replay. It belongs on the root so the whole subtree is
    covered.
  -->
  <OverlayShell
    v-if="open && displayScope"
    data-statsig-block
    label="Focus session"
    @request-close="requestClose"
  >
    <SessionPlan
      v-if="state.phase === 'planning'"
      :scope="displayScope"
      :mode="state.mode"
      :goal-type="state.goalType"
      :goal-index="state.goalIndex"
      :goal-marks="state.goalMarks"
      :capacity-minutes="capacityMinutes"
      :papers="PAPERS"
      @set-mode="dispatch({ type: 'setMode', mode: $event as SessionMode })"
      @set-goal-type="
        dispatch({ type: 'setGoalType', goalType: $event as GoalType })
      "
      @set-goal-index="dispatch({ type: 'setGoalIndex', index: $event })"
      @set-goal-marks="dispatch({ type: 'setGoalMarks', marks: $event })"
      @confirm="onConfirmPlan"
      @close="requestClose"
    />

    <div v-else-if="state.phase === 'booting'" class="booting">
      <SessionBootLoader :mode="state.mode" :milestone="bootMilestone" />
      <!-- The loader states the failure honestly but offers no way out of it. -->
      <div v-if="bootMilestone === 'failed'" class="boot-recover">
        <p class="boot-error" role="alert">{{ bootError }}</p>
        <div class="boot-actions">
          <button type="button" class="button primary" @click="boot">
            Try again
          </button>
          <button type="button" class="button" @click="requestClose">
            Close
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="state.phase === 'focusing'" class="focusing">
      <!--
        Offered, never forced (PE-8.5): declining retires the offer for the rest
        of the session rather than asking again.
      -->
      <div v-if="state.breakOffered" class="offer">
        <span class="offer-copy">
          You've focused for a while. Take five, or keep going — your call.
        </span>
        <button type="button" class="button" @click="onTakeBreak">
          Take 5
        </button>
        <button
          type="button"
          class="button"
          @click="dispatch({ type: 'declineBreak' })"
        >
          Keep going
        </button>
      </div>

      <FocusBar
        :mode="state.mode === 'tutor' ? 'tutor' : 'question'"
        :scope-label="scopeLabel"
        :goal-label="goalLabel"
        :fraction="goalFraction(state, lockedCapacity)"
        :meta="focusMeta"
        @take-break="onTakeBreak"
        @end="onEnd"
      />

      <TutorPanel
        v-if="state.mode === 'tutor'"
        :session-id="sessionId ?? ''"
        :scope-label="scopeLabel"
        :opening-message="tutorOpening"
        @exchange="dispatch({ type: 'exchange' })"
        @activity="dispatch({ type: 'activity' })"
      />
      <QuestionPanel
        v-else
        :subject-id="displayScope.subjectId"
        :session-id="sessionId ?? ''"
        :topic-id="displayScope.topicId ?? ''"
        :first-question="firstQuestion"
        @answered="dispatch({ type: 'answered', marks: $event.marks })"
        @activity="dispatch({ type: 'activity' })"
      />

      <InactivityNudge
        :visible="state.nudgeVisible"
        @dismiss="dispatch({ type: 'dismissNudge' })"
      />
    </div>

    <!--
      Both actions cut the break short, which is the same fact about the
      student, so both record `break_skipped`.
    -->
    <BreakScreen
      v-else-if="state.phase === 'break'"
      :seconds-left="state.breakSecondsLeft"
      :total-seconds="config.breakSeconds"
      @skip="dispatch({ type: 'skipBreak', atMs: Date.now() })"
      @resume="dispatch({ type: 'skipBreak', atMs: Date.now() })"
    />

    <SessionComplete
      v-else-if="state.phase === 'complete'"
      :mode="state.mode"
      :scope-label="scopeLabel"
      :goal-met="state.endedReason === 'goal_met'"
      :minutes="completeMinutes"
      :questions-answered="state.questionsAnswered"
      :exchanges="state.exchanges"
      :marks-earned="state.marksEarned"
      :capacity-outcome="capacityOutcome"
      :attainment-change="attainmentChange"
      :stage-index="stageIndex"
      :stage-up="stageUp"
      :review-nudge="reviewNudge"
      @close="closeNow"
      @again="restart"
    />

    <!--
      Kept mounted through the expiry transition: MockRunner holds the typed
      answers and fires the submit itself, so unmounting it the moment the timer
      hits zero would lose the paper mid-request.
    -->
    <MockRunner
      v-else-if="
        state.phase === 'mockRunning' ||
        (state.phase === 'mockResults' && !mockResult)
      "
      :session-id="sessionId ?? ''"
      :questions="mockQuestions"
      :seconds-left="state.mockSecondsLeft"
      :total-seconds="mockTotalSeconds"
      @submitted="onMockSubmitted"
      @close="requestClose"
    />

    <MockResults
      v-else-if="state.phase === 'mockResults' && mockResult"
      :result="mockResult"
      :questions="mockQuestions"
      @close="closeNow"
      @again="restart"
    />

    <!--
      Closing mid-block throws away work the student just did, so it asks first.
      No countdown and no guilt — just the honest consequence.
    -->
    <div v-if="confirmClose" class="confirm-scrim">
      <RlCard>
        <div
          class="confirm rl-anim-pop"
          role="alertdialog"
          aria-label="Close session"
        >
          <p class="confirm-copy">{{ confirmCopy }}</p>
          <div class="confirm-actions">
            <button type="button" class="button" @click="confirmClose = false">
              Keep going
            </button>
            <button type="button" class="button primary" @click="closeNow">
              Close anyway
            </button>
          </div>
        </div>
      </RlCard>
    </div>
  </OverlayShell>
</template>

<style scoped>
.focusing {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.offer {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  padding: var(--sp-3) var(--sp-4);
  background: var(--rl-info-bg);
  border: 1px solid var(--rl-info-bd);
  border-radius: var(--radius);
}

.offer-copy {
  flex: 1;
  font-size: var(--rl-text-base);
  font-weight: 500;
  color: var(--rl-info-fg);
}

/* rl-qe6: fill the overlay rather than sit in its top-left corner. The
   height is the viewport less OverlayShell's body padding (top and sp-8
   bottom), so it centres without forcing a scrollbar. */
.booting {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100dvh - var(--overlay-body-pad-top) - var(--sp-8));
}

.boot-recover {
  max-width: 420px;
  margin: var(--sp-6) auto 0;
  text-align: center;
}

.boot-error {
  margin: 0 0 var(--sp-4);
  font-size: var(--rl-text-base);
  color: var(--rl-danger-fg);
}

.boot-actions {
  display: flex;
  gap: var(--sp-2);
  justify-content: center;
}

.confirm-scrim {
  position: fixed;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-6);
  background: rgba(255, 255, 255, 0.6);
}

.confirm {
  max-width: 380px;
}

.confirm-copy {
  margin: 0 0 var(--sp-4);
  font-size: var(--rl-text-base);
  line-height: 1.5;
  color: var(--rl-fg-1);
}

.confirm-actions {
  display: flex;
  gap: var(--sp-2);
  justify-content: flex-end;
}
</style>
